import {
  BankOutlined,
  FileTextOutlined,
  HomeOutlined,
  ReadOutlined,
  ScheduleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { agendaDetailPath, organizationDetailPath } from '../../../constants/routes';
import {
  MEETING_DISPLAY_STATUS_META,
  MEETING_TYPE_OPTIONS,
  meetingModeLabel,
  normalizeMeetingDisplayStatusKey,
  optionLabel,
} from '../../../constants/meetings';
import { formatJalaliDateTime, splitApiDateTime } from '../../../utils/dateDisplay';
import { transcriptSearchPath } from '../../../utils/searchHighlight';

export const SEARCH_TYPE_META = {
  meetings: {
    label: 'جلسات',
    icon: ScheduleOutlined,
    iconClass: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400',
    to: (item) => (item?.id ? `/meetings/${item.id}` : null),
  },
  rooms: {
    label: 'اتاق‌ها',
    icon: HomeOutlined,
    iconClass: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400',
    to: (item) => (item?.id ? `/rooms/${item.id}` : null),
  },
  minutes: {
    label: 'صورت جلسات',
    icon: FileTextOutlined,
    iconClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
    to: (item) => (item?.id ? `/minutes/${item.id}` : null),
  },
  agenda_items: {
    label: 'دستور جلسه',
    icon: UnorderedListOutlined,
    iconClass: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400',
    to: (item) => {
      const agendaId = item?.agenda_id || item?.agenda?.id;
      return agendaId ? agendaDetailPath(agendaId) : null;
    },
  },
  transcripts: {
    label: 'رونوشت‌ها',
    icon: ReadOutlined,
    iconClass: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
    to: (item) => transcriptSearchPath(item),
  },
  organizations: {
    label: 'سازمان‌ها',
    icon: BankOutlined,
    iconClass: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400',
    to: (item) => (item?.id ? organizationDetailPath(item.id) : null),
  },
};

function truncateText(value, max = 120) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function formatMeetingSchedule(meeting) {
  const start = splitApiDateTime(meeting?.starts_at);
  const end = splitApiDateTime(meeting?.ends_at);
  if (!start.date) return '';

  const sameDay = start.date && end.date && start.date === end.date;
  if (sameDay && start.time && end.time) {
    return `${start.date} · ${start.time} ← ${end.time}`;
  }
  if (start.time) {
    return `${start.date} ${start.time}`;
  }
  return start.date;
}

function meetingTitleFromAgendaItem(item) {
  return item?.agenda?.meeting?.title || item?.agenda?.meeting_title || null;
}

export function getSearchResultTitle(type, item) {
  if (!item) return '—';
  if (type === 'meetings') return item.title || '—';
  if (type === 'rooms') return item.name || item.title || '—';
  if (type === 'minutes') return item.summary || item.title || 'صورت جلسه';
  if (type === 'agenda_items') return item.title || '—';
  if (type === 'transcripts') {
    const meetingTitle = item.meeting?.title || item.meeting_title || 'رونوشت';
    if (item.timecode) return `${meetingTitle} – ${item.timecode}`;
    return meetingTitle === 'رونوشت' && item.language ? `رونوشت (${item.language})` : meetingTitle;
  }
  if (type === 'organizations') return item.name || '—';
  return item.title || item.name || '—';
}

export function getSearchResultSubtitle(type, item) {
  if (!item) return '';
  if (type === 'meetings') {
    const parts = [];
    const schedule = formatMeetingSchedule(item);
    if (schedule) parts.push(schedule);
    if (item.host?.name) parts.push(`میزبان: ${item.host.name}`);
    else if (item.host_name) parts.push(`میزبان: ${item.host_name}`);
    if (item.room?.name) parts.push(`اتاق: ${item.room.name}`);
    if (item.organization?.name) parts.push(item.organization.name);
    const extra = truncateText(item.objective || item.description);
    if (extra) parts.push(extra);
    return parts.join(' · ');
  }
  if (type === 'rooms') {
    const parts = [item.location, item.room_type].filter(Boolean);
    if (item.capacity) parts.push(`ظرفیت ${item.capacity}`);
    if (item.description) parts.push(truncateText(item.description, 80));
    return parts.join(' · ');
  }
  if (type === 'minutes') {
    const parts = [];
    const meetingTitle = item.meeting?.title || item.meeting_title;
    if (meetingTitle) parts.push(`جلسه: ${meetingTitle}`);
    const dateVal = item.created_at || item.updated_at;
    if (dateVal) parts.push(formatJalaliDateTime(dateVal));
    const contentSnippet = truncateText(item.content, 100);
    if (contentSnippet) parts.push(contentSnippet);
    return parts.join(' · ');
  }
  if (type === 'agenda_items') {
    const parts = [];
    const meetingTitle = meetingTitleFromAgendaItem(item);
    if (meetingTitle) parts.push(`جلسه: ${meetingTitle}`);
    const desc = item.description?.trim();
    if (desc) parts.push(truncateText(desc, 100));
    else if (item.item_type) parts.push(item.item_type);
    if (item.created_at) parts.push(formatJalaliDateTime(item.created_at));
    return parts.join(' · ');
  }
  if (type === 'transcripts') {
    const parts = [];
    if (item.snippet) parts.push(item.snippet);
    if (item.match_count > 1) parts.push(`${item.match_count} مورد`);
    if (item.transcript_type) parts.push(item.transcript_type);
    if (item.word_count) parts.push(`${item.word_count} کلمه`);
    if (item.created_at) parts.push(formatJalaliDateTime(item.created_at));
    return parts.join(' · ');
  }
  if (type === 'organizations') {
    const parts = [];
    const managerName = item.manager?.name?.trim?.() || item.manager?.name || '';
    const managerContact = item.manager?.mobile || item.manager?.email || null;
    if (managerName) parts.push(`مدیر: ${managerName}`);
    if (managerContact) {
      parts.push(managerName ? managerContact : `مدیر: ${managerContact}`);
    }
    if (item.domain) parts.push(item.domain);
    if (item.industry) parts.push(item.industry);
    return parts.join(' · ');
  }
  return truncateText(item.description?.trim());
}

export function getSearchResultTags(type, item) {
  if (!item) return [];
  if (type === 'meetings') {
    const statusKey = normalizeMeetingDisplayStatusKey(item.display_status || item.status);
    const status = MEETING_DISPLAY_STATUS_META[statusKey];
    const tags = [];
    if (status) tags.push({ key: 'status', label: status.label, color: status.color });
    if (item.type) tags.push({ key: 'type', label: optionLabel(MEETING_TYPE_OPTIONS, item.type) });
    if (item.meeting_group?.name) {
      tags.push({ key: 'meeting-group', label: `کارگروه: ${item.meeting_group.name}`, color: 'cyan' });
    }
    if (item.room_reservation_state === 'needs_reassignment') {
      tags.push({ key: 'room-relocation', label: 'نیازمند جابه‌جایی اتاق', color: 'red' });
    }
    const mode = meetingModeLabel(item);
    if (mode && item.mode !== 'private') tags.push({ key: 'mode', label: mode });
    return tags;
  }
  if (type === 'rooms' && item.is_active === false) {
    return [{ key: 'inactive', label: 'غیرفعال' }];
  }
  if (type === 'agenda_items') {
    const tags = [];
    if (item.is_important) tags.push({ key: 'important', label: 'مهم', color: 'orange' });
    if (item.is_done) tags.push({ key: 'done', label: 'انجام‌شده', color: 'green' });
    return tags;
  }
  if (type === 'organizations') {
    const tags = [];
    if (item.is_active === false) tags.push({ key: 'inactive', label: 'غیرفعال' });
    if (item.is_personal) tags.push({ key: 'personal', label: 'شخصی' });
    return tags;
  }
  return [];
}

export function getSearchResultDetailPath(type, item) {
  const meta = SEARCH_TYPE_META[type];
  return meta?.to?.(item) || null;
}
